import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { GraduationCap, Calculator, Book, Brain } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './AiInEducationCostCalculatorPage/components/CalculationForm';
import ResultsOverview from './AiInEducationCostCalculatorPage/components/ResultsOverview';
import CostBreakdown from './AiInEducationCostCalculatorPage/components/CostBreakdown';
import QuickAdjustmentSidebar from './AiInEducationCostCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonSidebar from './AiInEducationCostCalculatorPage/components/ComparisonSidebar';
import TipsSidebar from './AiInEducationCostCalculatorPage/components/TipsSidebar';
import InfoContent from './AiInEducationCostCalculatorPage/components/InfoContent';
import RoiAnalysisDashboard from './AiInEducationCostCalculatorPage/components/RoiAnalysisDashboard';

// Определение начального состояния формы
export const initialFormData = {
  // Базовые параметры
  institutionType: 'university', // 'university', 'school', 'college', 'training_center'
  studentsCount: '5000',
  facultyCount: '300',
  aiIntegrationLevel: 'moderate', // 'basic', 'moderate', 'advanced', 'comprehensive'
  implementationPeriod: '12', // месяцев
  
  // Технические параметры
  infrastructureType: 'hybrid', // 'cloud', 'on_premise', 'hybrid'
  aiSolutionsCount: '3', // количество внедряемых ИИ-решений
  existingInfrastructureLevel: 'moderate', // 'minimal', 'moderate', 'substantial'
  customizationLevel: 'medium', // 'low', 'medium', 'high'
  
  // Обучение и поддержка
  trainingIntensity: 'medium', // 'low', 'medium', 'high'
  maintenanceLevel: 'standard', // 'minimal', 'standard', 'premium'
  
  // Дополнительные параметры
  includeDataPreparation: true,
  dataPreparationType: 'partial', // 'minimal', 'partial', 'comprehensive'
  includeConsulting: true,
  consultingType: 'standard', // 'basic', 'standard', 'advanced'
  includePostDeploymentSupport: true,
  supportDuration: '12', // месяцев
  
  // Параметры для расчета ROI
  existingCostsPerStudent: '500', // существующие расходы на одного студента
  expectedEfficiencyGain: '20', // ожидаемый прирост эффективности в %
  revenueImpact: 'moderate', // 'minimal', 'moderate', 'significant'
  costReductionFactor: 'medium', // 'low', 'medium', 'high'
  
  // Региональный фактор и валюта
  region: 'russia', // 'russia', 'europe', 'us', 'asia'
  currency: 'RUB', // 'RUB', 'USD', 'EUR'
};

// Определение типов образовательных учреждений
export const institutionTypes = [
  { id: 'university', name: 'Университет', defaultStudents: 5000, defaultFaculty: 300 },
  { id: 'school', name: 'Школа', defaultStudents: 1000, defaultFaculty: 80 },
  { id: 'college', name: 'Колледж', defaultStudents: 2000, defaultFaculty: 150 },
  { id: 'training_center', name: 'Учебный центр', defaultStudents: 500, defaultFaculty: 30 }
];

// Определение уровней интеграции ИИ
export const aiIntegrationLevels = [
  { id: 'basic', name: 'Базовый', description: 'Внедрение отдельных ИИ-инструментов для простых задач' },
  { id: 'moderate', name: 'Умеренный', description: 'Интеграция ИИ в ключевые образовательные процессы' },
  { id: 'advanced', name: 'Продвинутый', description: 'Полномасштабное внедрение ИИ с аналитикой данных' },
  { id: 'comprehensive', name: 'Комплексный', description: 'Трансформация всех аспектов учебного процесса с помощью ИИ' }
];

// Определение типов инфраструктуры
export const infrastructureTypes = [
  { id: 'cloud', name: 'Облачная', costFactor: 0.8 },
  { id: 'on_premise', name: 'Локальная', costFactor: 1.2 },
  { id: 'hybrid', name: 'Гибридная', costFactor: 1.0 }
];

// Определение уровней кастомизации
export const customizationLevels = [
  { id: 'low', name: 'Низкий', costFactor: 0.7 },
  { id: 'medium', name: 'Средний', costFactor: 1.0 },
  { id: 'high', name: 'Высокий', costFactor: 1.5 }
];

// Определение интенсивности обучения
export const trainingIntensityLevels = [
  { id: 'low', name: 'Базовое обучение', costFactor: 0.6 },
  { id: 'medium', name: 'Стандартное обучение', costFactor: 1.0 },
  { id: 'high', name: 'Интенсивное обучение', costFactor: 1.4 }
];

// Определение уровней поддержки
export const maintenanceLevels = [
  { id: 'minimal', name: 'Минимальная', costFactor: 0.5 },
  { id: 'standard', name: 'Стандартная', costFactor: 1.0 },
  { id: 'premium', name: 'Премиум', costFactor: 1.8 }
];

// Определение типов подготовки данных
export const dataPreparationTypes = [
  { id: 'minimal', name: 'Минимальная', costFactor: 0.5 },
  { id: 'partial', name: 'Частичная', costFactor: 1.0 },
  { id: 'comprehensive', name: 'Комплексная', costFactor: 1.7 }
];

// Определение типов консалтинга
export const consultingTypes = [
  { id: 'basic', name: 'Базовый', costFactor: 0.6 },
  { id: 'standard', name: 'Стандартный', costFactor: 1.0 },
  { id: 'advanced', name: 'Продвинутый', costFactor: 1.8 }
];

// Определение коэффициентов влияния на доход
export const revenueImpactFactors = [
  { id: 'minimal', name: 'Минимальное', factor: 0.05 },
  { id: 'moderate', name: 'Умеренное', factor: 0.15 },
  { id: 'significant', name: 'Значительное', factor: 0.30 }
];

// Определение факторов снижения затрат
export const costReductionFactors = [
  { id: 'low', name: 'Низкий', factor: 0.1 },
  { id: 'medium', name: 'Средний', factor: 0.2 },
  { id: 'high', name: 'Высокий', factor: 0.3 }
];

// Региональные коэффициенты
export const regionFactors = [
  { id: 'russia', name: 'Россия', factor: 1.0, currency: 'RUB' },
  { id: 'europe', name: 'Европа', factor: 1.3, currency: 'EUR' },
  { id: 'us', name: 'США', factor: 1.5, currency: 'USD' },
  { id: 'asia', name: 'Азия', factor: 0.9, currency: 'USD' }
];

/**
 * AiInEducationCostCalculatorPage Component - Калькулятор стоимости внедрения ИИ в образовательных учреждениях.
 */
const AiInEducationCostCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('ai-education-calculator') || {
    id: 'ai-education-calculator',
    name: 'Калькулятор стоимости ИИ в образовании',
    metaTitle: 'Калькулятор стоимости внедрения ИИ в образовании | Gensio',
    metaDescription: 'Рассчитайте затраты на внедрение искусственного интеллекта в образовательных учреждениях с учетом особенностей вашей организации, технической инфраструктуры и требований к обучению.',
  }, []);
  
  const pageTitle = tool?.metaTitle || 'Калькулятор стоимости ИИ в образовании | Gensio';
  const metaDescription = tool?.metaDescription || 'Рассчитайте затраты на внедрение искусственного интеллекта в образовательных учреждениях с учетом особенностей вашей организации, технической инфраструктуры и требований к обучению.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [costBreakdown, setCostBreakdown] = useState([]);
  const [showFullCostBreakdown, setShowFullCostBreakdown] = useState(false);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных расчетов из localStorage ---
  useEffect(() => {
    const savedCalcsData = localStorage.getItem('savedAiEducationCalcs');
    if (savedCalcsData) {
      try {
        setSavedCalculations(JSON.parse(savedCalcsData));
      } catch (e) {
        console.error("Error loading saved calculations:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'university':
          newState.institutionType = 'university';
          newState.studentsCount = '5000';
          newState.facultyCount = '300';
          break;
        case 'school':
          newState.institutionType = 'school';
          newState.studentsCount = '1000';
          newState.facultyCount = '80';
          break;
        case 'college':
          newState.institutionType = 'college';
          newState.studentsCount = '2000';
          newState.facultyCount = '150';
          break;
        case 'training-center':
          newState.institutionType = 'training_center';
          newState.studentsCount = '500';
          newState.facultyCount = '30';
          break;
        case 'basic-integration':
          newState.aiIntegrationLevel = 'basic';
          break;
        case 'advanced-integration':
          newState.aiIntegrationLevel = 'advanced';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setCostBreakdown([]);
    setErrors({});
    setShowFullCostBreakdown(false);

  }, [subToolId]);

  // Сохранение расчета в localStorage
  const saveCalculation = (name) => {
    if (!results) return;

    const newSavedCalc = {
      id: Date.now().toString(),
      name: name || `Расчет для ${getInstitutionTypeName(formData.institutionType)} на ${formData.studentsCount} студентов`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results }
    };

    const updatedSavedCalcs = [...savedCalculations, newSavedCalc];
    setSavedCalculations(updatedSavedCalcs);
    localStorage.setItem('savedAiEducationCalcs', JSON.stringify(updatedSavedCalcs));

    return newSavedCalc;
  };

  // Удаление сохраненного расчета
  const deleteSavedCalculation = (id) => {
    const updatedSavedCalcs = savedCalculations.filter(calc => calc.id !== id);
    setSavedCalculations(updatedSavedCalcs);
    localStorage.setItem('savedAiEducationCalcs', JSON.stringify(updatedSavedCalcs));
  };

  // Загрузка сохраненного расчета
  const loadSavedCalculation = (id) => {
    const calcToLoad = savedCalculations.find(calc => calc.id === id);
    if (calcToLoad) {
      setFormData(calcToLoad.formData);
      setResults(calcToLoad.results);
      // Для правильного отображения разбивки затрат мы должны перезапустить расчет
      // или сохранять costBreakdown в savedCalculations
    }
  };

  // Получение названия типа учреждения по ID
  const getInstitutionTypeName = (id) => {
    const institution = institutionTypes.find(type => type.id === id);
    return institution ? institution.name : id;
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Brain className="w-8 h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор стоимости ИИ в образовании'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              costBreakdown={costBreakdown}
              setCostBreakdown={setCostBreakdown}
              institutionTypes={institutionTypes}
              aiIntegrationLevels={aiIntegrationLevels}
              infrastructureTypes={infrastructureTypes}
              customizationLevels={customizationLevels}
              trainingIntensityLevels={trainingIntensityLevels}
              maintenanceLevels={maintenanceLevels}
              dataPreparationTypes={dataPreparationTypes}
              consultingTypes={consultingTypes}
              revenueImpactFactors={revenueImpactFactors}
              costReductionFactors={costReductionFactors}
              regionFactors={regionFactors}
              saveCalculation={saveCalculation}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} formData={formData} />
                <CostBreakdown 
                  costBreakdown={costBreakdown} 
                  results={results}
                  showFullCostBreakdown={showFullCostBreakdown}
                  setShowFullCostBreakdown={setShowFullCostBreakdown}
                  formData={formData}
                />
                <RoiAnalysisDashboard
                  results={results}
                  formData={formData}
                />
              </>
            )}

            {/* --- Информационный контент об ИИ в образовании --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              institutionTypes={institutionTypes}
              aiIntegrationLevels={aiIntegrationLevels}
              regionFactors={regionFactors}
            />

            {/* --- Панель сравнения расчетов --- */}
            <ComparisonSidebar 
              savedCalculations={savedCalculations}
              deleteSavedCalculation={deleteSavedCalculation}
              loadSavedCalculation={loadSavedCalculation}
              institutionTypes={institutionTypes}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar for Cost Breakdown Table */
        .cost-breakdown-table {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .cost-breakdown-table {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .cost-breakdown-table::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .cost-breakdown-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .cost-breakdown-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .cost-breakdown-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .cost-breakdown-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .cost-breakdown-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .cost-breakdown-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default AiInEducationCostCalculatorPage;